import { useRef, createContext, useContext, useReducer, useEffect, useCallback, forwardRef } from 'react';
import { useIsDebug } from '../hooks/useIsDebug';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// adapted from https://git.hubteam.com/HubSpot/media-default-modules/blob/master/jsr-modules/src/components/AbstractAudioPlayer.tsx

const createPlayerRegistry = () => {
  let audioEls = [];
  const registerAudioPlayer = audioEl => {
    if (!audioEls.includes(audioEl)) {
      audioEls.push(audioEl);
    }
  };
  const deregisterAudioPlayer = audioEl => {
    audioEls = audioEls.filter(el => el !== audioEl);
  };
  const activatePlayer = audioEl => {
    audioEls.forEach(el => {
      if (el !== audioEl) {
        el.pause();
      }
    });
  };
  document.addEventListener('keypress', e => {
    if (e.key === ' ') {
      audioEls.forEach(audioEl => {
        if (!audioEl.paused) {
          audioEl.pause();
        }
      });
    }
  });
  return {
    registerAudioPlayer,
    deregisterAudioPlayer,
    activatePlayer
  };
};
let audioPlayerRegistry;
const Json = ({
  data
}) => /*#__PURE__*/_jsx("div", {
  children: /*#__PURE__*/_jsx("pre", {
    children: JSON.stringify(data, null, 2)
  })
});
export const PlayerStatuses = {
  INITIAL: 'INITIAL',
  // audio has not yet been played
  LOADING: 'LOADING',
  // initial loading after interaction to play
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  WAITING: 'WAITING',
  // playback interrupted to load more data after initial play
  ENDED: 'ENDED',
  ERROR: 'ERROR'
};
const INITIAL_AUDIO_PLAYER_STATE = {
  status: PlayerStatuses.INITIAL,
  volume: 0,
  isMuted: false,
  duration: 0,
  readyState: 0,
  hasStarted: false,
  hasEnded: false,
  isSeeking: false,
  currentTime: 0,
  playbackRate: 1,
  secondChunks: {}
};
const AudioPlayerActions = {
  playerMounted: (volume, isMuted) => ({
    type: 'PLAYER_MOUNTED',
    payload: {
      volume,
      isMuted
    }
  }),
  loadedMetadata: duration => ({
    type: 'LOADED_METADATA',
    payload: {
      duration
    }
  }),
  progressed: readyState => ({
    type: 'PROGRESSED',
    payload: {
      readyState
    }
  }),
  played: () => ({
    type: 'PLAYED'
  }),
  paused: () => ({
    type: 'PAUSED'
  }),
  ended: () => ({
    type: 'ENDED'
  }),
  startedWaitingForData: () => ({
    type: 'STARTED_WAITING_FOR_DATA'
  }),
  resumedAfterReceivingData: () => ({
    type: 'RESUMED_AFTER_RECEIVING_DATA'
  }),
  startedSeek: currentTime => ({
    type: 'STARTED_SEEK',
    payload: {
      currentTime
    }
  }),
  seeked: currentTime => ({
    type: 'SEEKED',
    payload: {
      currentTime
    }
  }),
  timeUpdated: currentTime => ({
    type: 'TIME_UPDATED',
    payload: {
      currentTime
    }
  }),
  volumeChanged: (volume, isMuted) => ({
    type: 'VOLUME_CHANGED',
    payload: {
      volume,
      isMuted
    }
  }),
  playbackRateChanged: playbackRate => ({
    type: 'RATE_CHANGED',
    payload: {
      playbackRate
    }
  }),
  error: () => ({
    type: 'ERROR'
  }),
  resetStatus: () => ({
    type: 'RESET_STATUS'
  })
};
const AudioPlayerReducer = (state, action) => {
  switch (action.type) {
    case 'PLAYER_MOUNTED':
      {
        return Object.assign({}, state, {
          volume: action.payload.volume,
          isMuted: action.payload.isMuted
        });
      }
    case 'LOADED_METADATA':
      {
        return Object.assign({}, state, {
          duration: action.payload.duration
        });
      }
    case 'PROGRESSED':
      {
        return Object.assign({}, state, {
          readyState: action.payload.readyState
        });
      }
    case 'PLAYED':
      {
        return Object.assign({}, state, {
          hasEnded: false
        });
      }
    case 'PAUSED':
      {
        return Object.assign({}, state, {
          status: state.status === PlayerStatuses.ERROR ? PlayerStatuses.ERROR : PlayerStatuses.PAUSED
        });
      }
    case 'ENDED':
      {
        return Object.assign({}, state, {
          status: PlayerStatuses.ENDED,
          hasEnded: true
        });
      }
    case 'STARTED_WAITING_FOR_DATA':
      {
        return Object.assign({}, state, {
          status: state.hasStarted ? PlayerStatuses.WAITING : PlayerStatuses.LOADING
        });
      }
    case 'RESUMED_AFTER_RECEIVING_DATA':
      {
        return Object.assign({}, state, {
          status: PlayerStatuses.PLAYING,
          hasStarted: true
        });
      }
    case 'STARTED_SEEK':
      {
        return Object.assign({}, state, {
          isSeeking: true,
          currentTime: action.payload.currentTime
        });
      }
    case 'SEEKED':
      {
        return Object.assign({}, state, {
          isSeeking: false,
          currentTime: action.payload.currentTime
        });
      }
    case 'TIME_UPDATED':
      {
        return Object.assign({}, state, {
          currentTime: action.payload.currentTime,
          secondChunks: Object.assign({}, state.secondChunks, {
            [Math.floor(action.payload.currentTime)]: true
          })
        });
      }
    case 'VOLUME_CHANGED':
      {
        return Object.assign({}, state, {
          volume: action.payload.volume,
          isMuted: action.payload.isMuted
        });
      }
    case 'RATE_CHANGED':
      {
        return Object.assign({}, state, {
          playbackRate: action.payload.playbackRate
        });
      }
    case 'ERROR':
      {
        return Object.assign({}, state, {
          status: PlayerStatuses.ERROR
        });
      }
    case 'RESET_STATUS':
      {
        return Object.assign({}, state, {
          status: PlayerStatuses.INITIAL
        });
      }
    default:
      {
        return state;
      }
  }
};
export const AudioPlayerContext = /*#__PURE__*/createContext({
  audioRef: null,
  audioPlayerState: null,
  audioPlayerDispatch: null
});
const useAudioPlayerContext = () => {
  const {
    audioRef,
    audioPlayerDispatch,
    audioPlayerState
  } = useContext(AudioPlayerContext);
  const isDebug = useIsDebug();
  if (!audioRef || !audioPlayerDispatch || !audioPlayerState) {
    throw new Error("Audio Player Provider wasn't setup correctly make sure you are passing the correct argument to the AudioPlayerProvider");
  }
  const wrappedDispatch = useCallback(action => {
    if (isDebug) {
      console.log('[hs-audio] audioPlayerDispatch:', action);
    }
    audioPlayerDispatch(action);
  }, [audioPlayerDispatch, isDebug]);
  return {
    audioPlayerDispatch: wrappedDispatch,
    audioRef,
    audioPlayerState
  };
};
export const AudioPlayerProvider = ({
  children,
  audioRef,
  duration = 0
}) => {
  const [audioPlayerState, audioPlayerDispatch] = useReducer(AudioPlayerReducer, Object.assign({}, INITIAL_AUDIO_PLAYER_STATE, {
    duration
  }));
  return /*#__PURE__*/_jsx(AudioPlayerContext.Provider, {
    value: {
      audioRef,
      audioPlayerState,
      audioPlayerDispatch
    },
    children: children
  });
};
function addListener(el, events, fn) {
  const listeners = events.split(' ');
  listeners.forEach(e => el.addEventListener(e, fn));
  return () => {
    listeners.forEach(e => el.removeEventListener(e, fn));
  };
}
const useSetupAudioPlayerEventListeners = () => {
  const {
    audioRef,
    audioPlayerDispatch
  } = useAudioPlayerContext();
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    if (!audioPlayerRegistry) {
      audioPlayerRegistry = createPlayerRegistry();
    }
    audioPlayerRegistry.registerAudioPlayer(audioEl);
    audioPlayerDispatch(AudioPlayerActions.playerMounted(audioEl.volume, audioEl.muted));
    const listeners = [addListener(audioEl, 'loadedmetadata', () => {
      const duration = audioEl.duration;
      audioPlayerDispatch(AudioPlayerActions.loadedMetadata(duration));
    }), addListener(audioEl, 'loadstart progress canplay canplaythrough', () => {
      const readyState = audioEl.readyState;
      audioPlayerDispatch(AudioPlayerActions.progressed(readyState));
    }), addListener(audioEl, 'play', () => {
      audioPlayerDispatch(AudioPlayerActions.played());
    }), addListener(audioEl, 'pause', () => {
      audioPlayerDispatch(AudioPlayerActions.paused());
    }), addListener(audioEl, 'ended', () => {
      audioPlayerDispatch(AudioPlayerActions.ended());
    }), addListener(audioEl, 'waiting', () => {
      audioPlayerDispatch(AudioPlayerActions.startedWaitingForData());
    }), addListener(audioEl, 'playing', () => {
      audioPlayerDispatch(AudioPlayerActions.resumedAfterReceivingData());
    }), addListener(audioEl, 'seeking', () => {
      const currentTime = audioEl.currentTime;
      audioPlayerDispatch(AudioPlayerActions.startedSeek(currentTime));
    }), addListener(audioEl, 'seeked', () => {
      const currentTime = audioEl.currentTime;
      audioPlayerDispatch(AudioPlayerActions.seeked(currentTime));
    }), addListener(audioEl, 'timeupdate', () => {
      const currentTime = audioEl.currentTime;
      audioPlayerDispatch(AudioPlayerActions.timeUpdated(currentTime));
    }), addListener(audioEl, 'volumechange', () => {
      const volume = audioEl.volume;
      const isMuted = audioEl.muted;
      audioPlayerDispatch(AudioPlayerActions.volumeChanged(volume, isMuted));
    }), addListener(audioEl, 'ratechange', () => {
      const playbackRate = audioEl.playbackRate;
      audioPlayerDispatch(AudioPlayerActions.playbackRateChanged(playbackRate));
    }), addListener(audioEl, 'error', () => {
      audioPlayerDispatch(AudioPlayerActions.error());
    })];
    return () => {
      listeners.forEach(remove => remove());
      audioPlayerRegistry.deregisterAudioPlayer(audioEl);
    };
  }, [audioRef, audioPlayerDispatch]);
};
export const usePlayer = () => {
  const {
    audioPlayerState,
    audioRef
  } = useAudioPlayerContext();
  const play = useCallback(async () => {
    if (!audioRef.current) {
      return;
    }
    audioPlayerRegistry.activatePlayer(audioRef.current);
    try {
      await audioRef.current.play();
    } catch (err) {
      console.error('Failed to play audio el:', audioRef.current, err);
    }
  }, [audioRef]);
  const pause = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.pause();
  }, [audioRef]);
  const seek = useCallback(val => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.currentTime = val;
  }, [audioRef]);
  const setVolume = useCallback(val => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.volume = val;
  }, [audioRef]);
  const setMuted = useCallback(muted => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.muted = muted;
  }, [audioRef]);
  const setPlaybackRate = useCallback(playbackRate => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.playbackRate = playbackRate;
  }, [audioRef]);
  return {
    state: audioPlayerState,
    actions: {
      play,
      pause,
      seek,
      setMuted,
      setVolume,
      setPlaybackRate
    }
  };
};
const Debug = () => {
  const player = usePlayer();
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(Json, {
      data: player.state
    })
  });
};
export const Html5Player = /*#__PURE__*/forwardRef(({
  autoPlay,
  controls,
  src
}, ref) => {
  useSetupAudioPlayerEventListeners();
  const {
    audioPlayerDispatch
  } = useAudioPlayerContext();
  useEffect(() => {
    audioPlayerDispatch(AudioPlayerActions.resetStatus());
  }, [audioPlayerDispatch, src]);
  return /*#__PURE__*/_jsx("audio", {
    ref: ref,
    autoPlay: autoPlay,
    controls: controls,
    preload: "metadata" // this differs from media-default-modules version because there's no initial state
    ,
    src: src,
    "data-test-id": "audio-el"
  });
});
Html5Player.displayName = 'Html5Player';
export const AbstractAudioPlayer = ({
  autoPlay,
  children,
  duration,
  src
}) => {
  const audioRef = useRef(null);
  const isDebug = useIsDebug();
  if (!src && !duration) {
    console.log('AbstractAudioPlayer requires both `src` and `duration` props`');
    return null;
  }
  return /*#__PURE__*/_jsxs(AudioPlayerProvider, {
    audioRef: audioRef,
    duration: duration,
    children: [children, /*#__PURE__*/_jsx(Html5Player, {
      ref: audioRef,
      autoPlay: autoPlay,
      controls: isDebug,
      src: src
    }), isDebug && /*#__PURE__*/_jsx(Debug, {})]
  });
};